import { Observable, fromEvent, merge } from 'rxjs';
import { CameraService } from 'src/app/engine/camera.service';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { map, pairwise, debounceTime, filter, startWith, flatMap } from 'rxjs/operators';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { vec3 } from 'gl-matrix';
import { Store } from '@ngxs/store';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { TriggerEnum } from '../models/trigger.enum';

interface CameraStateModel {
    camera: CameraModel;
    zoom: number;
}

export function createTrigger(
    name: TriggerEnum,
    store: Store,
    engineService: EngineService,
    cameraService: CameraService,
    touch: boolean): Observable<any> {
    switch (name) {
        case TriggerEnum.CameraZoom:
            return touch
                ? createCameraZoomTrigger(engineService, cameraService)
                : createUserCameraZoomTrigger(engineService, cameraService);
        case TriggerEnum.CameraPan:
            return createUserCameraPanTrigger(engineService, cameraService);
        case TriggerEnum.CameraRotate:
            return touch
                ? createCameraRotateTrigger(engineService, cameraService)
                : createUserCameraRotateTrigger(store, engineService, cameraService);
        case TriggerEnum.NodeSelection:
            return createNodeSelectionTrigger(store);
        default:
            break;
    }
}

export function createCameraZoomTrigger(
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 1000,
    threshold: number = 0.001): Observable<any> {
    return getCameraRested(engineService)
        .pipe(
            startWith(cameraService.getCameraZoom()),
            map(() => cameraService.getCameraZoom()),
            pairwise(),
            map(([lastZoom, currentZoom]) => currentZoom - lastZoom),
            filter((delta) => Math.abs(delta) > threshold),
            debounceTime(throttle)
        );
}

export function createUserCameraZoomTrigger(
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 1000,
    threshold: number = 0.001): Observable<any> {
    const canvas = engineService.getCanvas();

    // user events
    const mouseWheel$ = fromEvent(canvas, 'wheel');
    const touchMove$ = fromEvent(canvas, 'touchmove')
        .pipe(
            filter((e: TouchEvent) => e.touches.length === 2)
        );

    const zoomTrigger$ = createCameraZoomTrigger(engineService, cameraService, throttle, threshold);
    
    // combine mouse + touch events
    return merge(
        mouseWheel$,
        touchMove$
    )
        .pipe(
            // map to camera movement
            flatMap(() => zoomTrigger$)
        );
}

export function createCameraRotateTrigger(
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 125): Observable<any> {
    return getCameraRested(engineService)
        .pipe(
            startWith(cameraService.getLookDirection()),
            map(() => cameraService.getLookDirection()),            
            pairwise(),
            map(([lastDirection, currentDirection]) => calcCameraRotating(lastDirection, currentDirection)),
            filter((rotatating) => rotatating),
            debounceTime(throttle)
        );
}

export function createUserCameraRotateTrigger(
    store: Store,
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 1000):
    Observable<any> {
    const canvas = engineService.getCanvas();

    // user events
    const mouseMove$ = fromEvent(canvas, 'mousemove')
        .pipe(
            filter((e: MouseEvent) => e.buttons === 1)
        );
    const touchMove$ = fromEvent(canvas, 'touchmove')
        .pipe(
            filter((e: TouchEvent) => e.touches.length === 1)
    );

    const rotateTrigger$ = createCameraRotateTrigger(engineService, cameraService, throttle);

    // combine mouse + touch events
    return merge(
        mouseMove$,
        touchMove$
    )
        .pipe(
            // map to camera movement
            flatMap(() => rotateTrigger$),
            filter(() => store.selectSnapshot(EngineCameraState.getPanMode) === 'rotate'),
            debounceTime(125)
        );
}

export function createCameraPanTrigger(
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 125): Observable<any> {
    return getCameraRested(engineService)
        .pipe(
            startWith(getCameraState(cameraService)),
            map(() => getCameraState(cameraService)),
            debounceTime(throttle),
            pairwise(),
            map(([lastCameraState, currentCameraState]) => calcCameraPanning(lastCameraState, currentCameraState)),
            filter((panning) => panning)
        );
}

export function createUserCameraPanTrigger(
    engineService: EngineService,
    cameraService: CameraService,
    throttle: number = 125): Observable<any> {
    const canvas = engineService.getCanvas();

    // user events
    const mousePan = fromEvent(canvas, 'mousemove')
        .pipe(
            filter((e: MouseEvent) => {
                return (e.buttons === 1 && e.shiftKey) // shift + left mouse
                    || e.buttons === 2 // right mouse
                    || e.buttons === 4; // aux (wheel)
            })
        );
    const touchPan = fromEvent(canvas, 'touchmove')
        .pipe(
            filter((e: TouchEvent) => e.touches.length === 2)
        );

    const panTrigger$ = createCameraPanTrigger(engineService, cameraService, throttle);

    // combine mouse + touch events
    return merge(
        mousePan,
        touchPan
    )
        .pipe(
            // map to camera movement
            flatMap(() => panTrigger$)
        );
}

export function createNodeSelectionTrigger(store: Store): Observable<any> {
    return store.select(EngineNodesState.getCheckedNodes)
        .pipe(
            pairwise(),
            filter(([lastNodes, currentNodes]) => currentNodes.length > 0)
        );
}

function getCameraState(cameraService: CameraService): CameraStateModel {
    return {
        camera: cameraService.getCamera(),
        zoom: cameraService.getCameraZoom()
    };
}

function getCameraRested(engineService: EngineService) {
    return engineService.getEvent(EventsEnum.CameraRested);
}

function getEyeVector(camera: CameraModel) {
    const { eye, look } = camera;
    const eyeVec = vec3.fromValues(eye.x, eye.y, eye.z);
    const lookVec = vec3.fromValues(look.x, look.y, look.z);
    const outVec = vec3.create();
    vec3.sub(outVec, eyeVec, lookVec);

    return outVec;
}

function calcCameraRotating(lastDirection: [number, number, number], currentDirection: [number, number, number]): boolean {
    const rotateTolerance = 0.001;
    const rotateAngle = vec3.angle(lastDirection, currentDirection);
    return rotateAngle > rotateTolerance;
}

function calcCameraPanning(lastCameraState: CameraStateModel, currentCameraState: CameraStateModel): boolean {
    const panTolerance = 1;
    const zoomTolerance =  0.001;
    
    const currentVec = getEyeVector(lastCameraState.camera);
    const updatedVec = getEyeVector(currentCameraState.camera);

    // Get 'look' -> 'eye' vector
    const panVec = vec3.create();
    vec3.sub(panVec, currentVec, updatedVec);

    const panDelta = (panVec as any[]) // Added `as any[]` to resolve Typescript 3.9.4 compilation issue
        .reduce((previousValue: number, currentValue: number) => previousValue + currentValue);

    const zoomDelta = currentCameraState.zoom - lastCameraState.zoom;

    if (zoomDelta === 0 && panDelta === 0) {
        return false;
    } else {
        return Math.abs(panDelta) < panTolerance
            && Math.abs(zoomDelta) < zoomTolerance; // account for minute zoom on mobile (pinch to zoom)
    }
}
